import React from 'react';

import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import { cssMap } from '@atlaskit/css';
import CopyIcon from '@atlaskit/icon/core/copy';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import Link from '@atlaskit/link/link';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { SAIN_CITATION_SELECTOR } from '../../../ai-answer-dialog/ai-answer-body/ai-answer-result/rendered/citation-selectors';
import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import { onAIAnswerCopyButtonClicked } from '../../../ai-answer-dialog/on-ai-answer-copy-button-clicked';
import { useSmartAnswerCardFeedbackOptions } from '../../../ai-answer-dialog/smart-answer-card/use-smart-answer-card-feedback-options';
import { SainAction } from '../../../common/controller/analytics/hooks/sain-action';
import { useSAINUsageCriteriaMet } from '../../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../../common/controller/analytics/use-analytics';
import { AIFeedback as AIFeedbackV3 } from '../../../common/ui/ai-feedback-v3/ai-feedback';
import { ATLASSIAN_INTELLIGENCE_TRUST_LINK } from '../../../constants';
import { OnlineDebuggerButton } from '../../online-debugger/online-debugger-button';
import { SAINMoreOptionsDropdown } from './sain-more-options-dropdown';

const messages = defineMessages({
	ai_content_disclaimer: {
		id: 'search_ai.ai_content_disclaimer',
		defaultMessage: 'Uses AI. Verify results.',
		description: 'Text for the AI content disclaimer',
	},
	ai_answer_component_copy_button: {
		id: 'search_ai.ai_answer_component_copy_button',
		defaultMessage: 'Copy',
		description: 'Button text for copying the ai answer string result',
	},
});

const copyIcon = () => <CopyIcon color={token('color.icon.subtlest')} label="copy" />;

type AIDisclaimerAndFeedbackProps = {
	showFeedbackButtons: boolean;
	showCopyButton?: boolean;
	showAIUsageDisclaimer?: boolean;
	copyText?: string;
	renderedContentRef?: React.RefObject<HTMLDivElement>;
};

const styles = cssMap({
	heightWrapper: {
		height: '24px',
	},
});

export function AIDisclaimerAndFeedback({
	showFeedbackButtons,
	showCopyButton,
	showAIUsageDisclaimer = true,
	copyText,
	renderedContentRef,
}: AIDisclaimerAndFeedbackProps): React.JSX.Element {
	const { commonAttributes, isAnswered } = useAIAnswerContext();
	const [fireAnalyticsEvent] = useAnalytics();
	const { formatMessage } = useIntl();

	const { answerString } = commonAttributes;

	const feedbackOptions = useSmartAnswerCardFeedbackOptions();

	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();

	const isArizeGateOn = fg('kd_sain_enable_arize_hello_only');
	const showOnlineDebuggerButton = isAnswered && fg('kd_sain_online_debugger');
	const showPromptOverrideButton = fg('convo_ai_sain_enable_prompt_override_hello_only');

	const hasMoreOptions = isAnswered && (isArizeGateOn || showPromptOverrideButton);

	const showOnlineDebuggerInline = showOnlineDebuggerButton && !hasMoreOptions;

	const onLinkClickHandler = (event: React.MouseEvent<HTMLAnchorElement>) => {
		event.stopPropagation();
	};

	const onLikeButtonClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
		feedbackOptions.onLikeButtonClickHandler();
	};

	const onDislikeButtonClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
		feedbackOptions.onDislikeButtonClickHandler();
	};

	const onDeselectLikeButtonClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
		feedbackOptions.onDeselectLikeButtonClickHandler();
	};

	const onDeselectDislikeButtonClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
		feedbackOptions.onDeselectDislikeButtonClickHandler();
	};

	const onCopyButtonClickHandler = async (event: React.MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
		fireSAINUsageEvent({ metCriteria: SainAction.COPY_CLICKED, commonAttributes });
		fireAnalyticsEvent(onAIAnswerCopyButtonClicked(commonAttributes));
		if (copyText) {
			await navigator.clipboard.writeText(copyText);
		} else {
			const plainText = answerString
				.replace(/\s*\[\^\d+\^\]\s*/g, ' ')
				.replace(/  +/g, ' ')
				.trim();
			try {
				const node = renderedContentRef?.current;
				if (node) {
					const clonedNode = node.cloneNode(true) as HTMLElement;
					clonedNode.querySelectorAll(SAIN_CITATION_SELECTOR).forEach((el) => el.remove());
					const htmlBlob = new Blob([clonedNode.innerHTML], { type: 'text/html' });
					const textBlob = new Blob([plainText], { type: 'text/plain' });
					await navigator.clipboard.write([
						new ClipboardItem({
							'text/html': htmlBlob,
							'text/plain': textBlob,
						}),
					]);
				} else {
					await navigator.clipboard.writeText(plainText);
				}
			} catch {
				await navigator.clipboard.writeText(plainText);
			}
		}
	};

	return (
		<Inline alignBlock="end" grow="fill" spread="space-between" xcss={styles.heightWrapper}>
			<Inline alignBlock="center" space="space.025">
				{showAIUsageDisclaimer ? (
					<>
						<StatusInformationIcon
							color={token('color.icon.subtle')}
							label=""
							size="small"
							spacing="compact"
						/>
						<Text size="small">
							<Link
								appearance="subtle"
								href={ATLASSIAN_INTELLIGENCE_TRUST_LINK}
								onClick={onLinkClickHandler}
								rel="noopener noreferrer"
							>
								<FormattedMessage {...messages.ai_content_disclaimer} />
							</Link>
						</Text>
					</>
				) : null}
			</Inline>
			<Inline alignBlock="center" space="space.050">
				{showFeedbackButtons ? (
					<AIFeedbackV3
						entrypointId={feedbackOptions.entrypointId}
						userDetails={feedbackOptions.userDetails}
						attributes={feedbackOptions.attributes}
						attributesWithConsent={feedbackOptions.attributesWithConsent}
						onLikeButtonClickHandler={onLikeButtonClickHandler}
						onDislikeButtonClickHandler={onDislikeButtonClickHandler}
						onDeselectLikeButtonClickHandler={onDeselectLikeButtonClickHandler}
						onDeselectDislikeButtonClickHandler={onDeselectDislikeButtonClickHandler}
						onFeedbackSubmitHandler={feedbackOptions.onFeedbackSubmitHandler}
					/>
				) : null}
				{showCopyButton ? (
					<IconButton
						icon={copyIcon}
						label={formatMessage(messages.ai_answer_component_copy_button)}
						onClick={onCopyButtonClickHandler}
						appearance="subtle"
						isTooltipDisabled={false}
						spacing="compact"
						shape="circle"
						testId="copy-button"
					/>
				) : null}
				{showOnlineDebuggerInline && <OnlineDebuggerButton />}
				{hasMoreOptions && <SAINMoreOptionsDropdown />}
			</Inline>
		</Inline>
	);
}
